import type { TrackKind, UserSettings } from '@etown/shared';
import { useMediaStore } from '../stores/mediaStore';

/**
 * Kamerabild klein und sparsam; `facingMode: 'user'` waehlt auf dem Handy
 * die Frontkamera (am Schreibtisch ohne Wirkung — ein Wunsch, kein Zwang).
 */
const KAMERA_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 320 },
  height: { ideal: 240 },
  frameRate: { ideal: 24 },
  facingMode: 'user',
};

type TrackListener = (kind: TrackKind, track: MediaStreamTrack | null) => void;
type AudioProcessing = Pick<UserSettings, 'noiseSuppression' | 'echoCancellation' | 'autoGainControl'>;

/**
 * Verwaltung der eigenen Medien-Tracks (Mikrofon, Kamera, Bildschirm).
 *
 * Mic/Cam folgen einer Soll-/Ist-Trennung: die HUD-Buttons schalten nur die
 * Bereitschaft ("armed") — tatsaechlich aufgenommen wird erst, wenn der
 * RtcManager Capture freigibt (mindestens ein Gespraechspartner mit
 * Medien-Freigabe). Verlaesst der letzte Peer die Reichweite, stoppen die
 * Tracks wieder (Kamera-LED aus). Screen bleibt Sofort-Capture, weil
 * getDisplayMedia eine User-Geste erfordert.
 */
class LocalMedia {
  private tracks: Partial<Record<TrackKind, MediaStreamTrack>> = {};
  private listeners = new Set<TrackListener>();
  private armed = { mic: false, cam: false };
  private captureAllowed = false;
  private audioProcessing: AudioProcessing = {
    noiseSuppression: true,
    echoCancellation: true,
    autoGainControl: true,
  };
  /** Serialisiert alle Capture-Uebergaenge (kein paralleles getUserMedia). */
  private reconcileChain: Promise<void> = Promise.resolve();

  onTrackChange(listener: TrackListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getTrack(kind: TrackKind): MediaStreamTrack | null {
    return this.tracks[kind] ?? null;
  }

  /** Lokale Vorschau der Kamera (eigene Bubble/Selbstansicht). */
  getStream(kind: TrackKind): MediaStream | null {
    const track = this.tracks[kind];
    return track ? new MediaStream([track]) : null;
  }

  isArmed(kind: 'mic' | 'cam'): boolean {
    return this.armed[kind];
  }

  /**
   * Bereitschaft schalten (HUD-Toggle). Beim Aktivieren laeuft ein
   * Permission-Probe in der User-Geste: der Browser-Dialog erscheint sofort
   * beim Klick — nicht erst mitten in einer Begegnung.
   */
  async setArmed(kind: 'mic' | 'cam', armed: boolean): Promise<void> {
    if (this.armed[kind] === armed) return;
    if (armed && !this.captureAllowed) {
      const probe = await navigator.mediaDevices.getUserMedia(
        kind === 'mic' ? { audio: true } : { video: true },
      );
      for (const track of probe.getTracks()) track.stop();
    }
    this.armed[kind] = armed;
    this.publishState();
    await this.reconcile();
  }

  /** Vom RtcManager: mindestens ein Peer mit Medien-Freigabe vorhanden? */
  setCaptureAllowed(allowed: boolean): void {
    if (this.captureAllowed === allowed) return;
    this.captureAllowed = allowed;
    void this.reconcile();
  }

  /** Audio-Verarbeitung (Rauschunterdrueckung etc.) — live auf den Mic-Track. */
  applyAudioSettings(settings: AudioProcessing): void {
    this.audioProcessing = {
      noiseSuppression: settings.noiseSuppression,
      echoCancellation: settings.echoCancellation,
      autoGainControl: settings.autoGainControl,
    };
    const mic = this.tracks.mic;
    if (mic) {
      void mic.applyConstraints({ ...this.audioProcessing }).catch(() => {
        // Nicht jedes Geraet unterstuetzt jede Verarbeitung — still ignorieren
      });
    }
  }

  async startScreen(): Promise<void> {
    if (this.tracks.screen) return;
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 12 } },
      audio: false,
    });
    const track = stream.getVideoTracks()[0] ?? null;
    if (track) {
      // Nutzer beendet das Teilen ueber den Browser-Dialog
      track.addEventListener('ended', () => this.stopTrack('screen'));
    }
    this.setTrack('screen', track);
    this.publishState();
  }

  stopScreen(): void {
    this.stopTrack('screen');
    this.publishState();
  }

  stopAll(): void {
    this.armed = { mic: false, cam: false };
    for (const kind of ['mic', 'cam', 'screen'] as const) this.stopTrack(kind);
    this.publishState();
  }

  /** Soll-Zustand (armed && captureAllowed) mit dem Ist-Zustand abgleichen. */
  private reconcile(): Promise<void> {
    this.reconcileChain = this.reconcileChain.then(() => this.reconcileNow());
    return this.reconcileChain;
  }

  private async reconcileNow(): Promise<void> {
    for (const kind of ['mic', 'cam'] as const) {
      const wanted = this.armed[kind] && this.captureAllowed;
      const hasTrack = this.tracks[kind] !== undefined;
      if (wanted && !hasTrack) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(
            kind === 'mic'
              ? { audio: { ...this.audioProcessing } }
              : { video: KAMERA_CONSTRAINTS },
          );
          this.setTrack(kind, kind === 'mic' ? stream.getAudioTracks()[0] ?? null : stream.getVideoTracks()[0] ?? null);
        } catch {
          // Berechtigung mitten in der Begegnung entzogen: Bereitschaft
          // zuruecknehmen, das Gespraech (Chat/DataChannel) laeuft weiter
          this.armed[kind] = false;
          useMediaStore.getState().setLocal({ mediaError: 'Zugriff auf Kamera/Mikrofon verweigert' });
        }
      } else if (!wanted && hasTrack) {
        this.stopTrack(kind);
      }
    }
    this.publishState();
  }

  private publishState(): void {
    useMediaStore.getState().setLocal({
      micArmed: this.armed.mic,
      camArmed: this.armed.cam,
      micLive: this.tracks.mic !== undefined,
      camLive: this.tracks.cam !== undefined,
      screenOn: this.tracks.screen !== undefined,
    });
  }

  private setTrack(kind: TrackKind, track: MediaStreamTrack | null): void {
    if (!track) return;
    this.tracks[kind] = track;
    for (const listener of this.listeners) listener(kind, track);
  }

  private stopTrack(kind: TrackKind): void {
    const track = this.tracks[kind];
    if (!track) return;
    track.stop();
    delete this.tracks[kind];
    for (const listener of this.listeners) listener(kind, null);
  }
}

/** App-weite Singleton-Instanz. */
export const localMedia = new LocalMedia();
